import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../../api/axios'
import { Alert } from '../../../components/ui/Alert'
import { selectClass } from '../../../components/ui/FormField'
import { nomePf } from '../divisoes'
import { usePistas } from './AbaPistas'
import { useSquads, type InscritoIpsc } from './AbaSquads'
import { cartao, erroApi, n4, td, th } from './comum'

// Lancamento do resultado de cada atirador numa pista (A/C/D/Miss/No-shoot/Procedimental/tempo/DQ).
// A API calcula pontos e Hit Factor e refaz a apuracao da etapa.

interface ResultadoPista {
  qtd_a: number; qtd_c: number; qtd_d: number; qtd_miss: number; qtd_no_shoot: number; qtd_procedural: number
  tempo_segundos: string | null; pontos: number; hit_factor: string; dq: boolean
}
type Linha = InscritoIpsc & { resultado: ResultadoPista | null }
type Campos = { a: string; c: string; d: string; miss: string; ns: string; proc: string; tempo: string; dq: boolean }

const CAMPOS: Array<[keyof Campos, string]> = [['a', 'A'], ['c', 'C'], ['d', 'D'], ['miss', 'Miss'], ['ns', 'No-shoot'], ['proc', 'Proced.'], ['tempo', 'Tempo (s)']]
const deResultado = (r: ResultadoPista | null): Campos => r
  ? { a: String(r.qtd_a), c: String(r.qtd_c), d: String(r.qtd_d), miss: String(r.qtd_miss), ns: String(r.qtd_no_shoot), proc: String(r.qtd_procedural), tempo: r.tempo_segundos ?? '', dq: r.dq }
  : { a: '', c: '', d: '', miss: '', ns: '', proc: '', tempo: '', dq: false }

export function AbaLancamento({ idetapa }: { idetapa: number }) {
  const qc = useQueryClient()
  const { data: pistas = [] } = usePistas(idetapa)
  const { data: squads } = useSquads(idetapa)
  const [idpista, setIdpista] = useState<number | null>(null)
  const [squad, setSquad] = useState<string>('')
  const [edicao, setEdicao] = useState<Record<number, Campos>>({})
  const [alerta, setAlerta] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const ativas = pistas.filter((p) => p.ativo)

  useEffect(() => { if (!idpista && ativas[0]) setIdpista(ativas[0].idpista) }, [ativas, idpista])

  const { data } = useQuery({
    queryKey: ['ipsc-resultados-pista', idpista],
    queryFn: () => api.get<{ data: { inscritos: Linha[] } }>(`/ipsc/pistas/${idpista}/resultados`).then((r) => r.data.data),
    enabled: !!idpista,
  })
  // ao trocar de pista (ou recarregar), os campos voltam a ser os gravados
  useEffect(() => {
    setEdicao(Object.fromEntries((data?.inscritos ?? []).map((l) => [l.idinscricao, deResultado(l.resultado)])))
  }, [data])

  const linhas = useMemo(() => (data?.inscritos ?? []).filter((l) => !squad || String(l.idsquad ?? 'sem') === squad), [data, squad])
  const lancados = (data?.inscritos ?? []).filter((l) => l.resultado).length

  const atualizar = () => {
    qc.invalidateQueries({ queryKey: ['ipsc-resultados-pista', idpista] })
    qc.invalidateQueries({ queryKey: ['ipsc-apuracao', idetapa] })
    qc.invalidateQueries({ queryKey: ['ipsc-pistas', idetapa] })
  }
  const salvar = useMutation({
    mutationFn: ({ l, c }: { l: Linha; c: Campos }) => api.put(`/ipsc/pistas/${idpista}/resultados/${l.idinscricao}`, {
      qtd_a: Number(c.a) || 0, qtd_c: Number(c.c) || 0, qtd_d: Number(c.d) || 0, qtd_miss: Number(c.miss) || 0,
      qtd_no_shoot: Number(c.ns) || 0, qtd_procedural: Number(c.proc) || 0,
      tempo_segundos: c.tempo === '' ? null : Number(c.tempo.replace(',', '.')), dq: c.dq,
    }),
    onSuccess: (_r, { l }) => { atualizar(); setAlerta({ type: 'success', message: `Resultado de ${l.atirador} salvo` }) },
    onError: (e, { l }) => setAlerta({ type: 'error', message: `${l.atirador}: ${erroApi(e, 'erro ao salvar')}` }),
  })
  const apagar = useMutation({
    mutationFn: (l: Linha) => api.delete(`/ipsc/pistas/${idpista}/resultados/${l.idinscricao}`),
    onSuccess: (_r, l) => { atualizar(); setAlerta({ type: 'success', message: `Resultado de ${l.atirador} excluído` }) },
    onError: (e) => setAlerta({ type: 'error', message: erroApi(e, 'Erro ao excluir') }),
  })

  if (!ativas.length) {
    return <p className={`${cartao} text-center py-12 text-compumaq-textoSuave`}>Cadastre as pistas da etapa (aba Pistas) antes de lançar.</p>
  }
  const campo = 'w-16 px-2 py-2 text-center text-base rounded-lg border border-compumaq-dourado/25 bg-compumaq-preto text-compumaq-texto focus:outline-none focus:ring-2 focus:ring-compumaq-dourado'
  return (
    <div className="space-y-3">
      {alerta && <Alert type={alerta.type} message={alerta.message} onClose={() => setAlerta(null)} />}
      <div className={`${cartao} p-3 flex flex-wrap gap-3 items-end`}>
        <div className="flex flex-wrap gap-1" role="group" aria-label="Pista">
          {ativas.map((p) => (
            <button key={p.idpista} onClick={() => setIdpista(p.idpista)}
              className={`px-4 py-2 rounded-lg text-sm ${String(idpista) === String(p.idpista) ? 'bg-compumaq-dourado text-compumaq-texto font-semibold' : 'border border-compumaq-dourado/30 text-compumaq-textoSuave'}`}>
              P{p.numero} · {p.nome}
            </button>
          ))}
        </div>
        <div className="min-w-[160px]">
          <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Squad</label>
          <select className={selectClass} value={squad} onChange={(e) => setSquad(e.target.value)} aria-label="Filtrar squad">
            <option value="">Todos</option>
            {squads?.squads.map((s) => <option key={s.idsquad} value={s.idsquad}>Squad {s.numero}{s.horario ? ` (${s.horario})` : ''}</option>)}
            <option value="sem">Sem squad</option>
          </select>
        </div>
        <p className="text-xs text-compumaq-textoSuave pb-2">{lancados} de {data?.inscritos.length ?? 0} lançado(s) nesta pista</p>
      </div>

      <div className={`${cartao} overflow-x-auto`}>
        <table className="w-full text-sm">
          <thead className="bg-compumaq-preto border-b border-compumaq-dourado/25">
            <tr>
              <th className={th}>Atirador</th>
              {CAMPOS.map(([k, nome]) => <th key={k} className={`${th} text-center`}>{nome}</th>)}
              <th className={`${th} text-center`}>DQ</th>
              <th className={`${th} text-right`}>Pontos / HF</th>
              <th className={th}></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-compumaq-dourado/25">
            {linhas.map((l) => {
              const c = edicao[l.idinscricao] ?? deResultado(null)
              const set = (k: keyof Campos, v: string | boolean) => setEdicao((prev) => ({ ...prev, [l.idinscricao]: { ...c, [k]: v } }))
              return (
                <tr key={l.idinscricao} data-linha={l.idinscricao} className={l.resultado ? '' : 'bg-compumaq-dourado/5'}>
                  <td className={td}>
                    <span className="font-medium text-compumaq-texto">{l.atirador}</span>
                    <span className="block text-xs text-compumaq-textoSuave">
                      {l.divisao ?? 'sem divisão'} {nomePf(l.power_factor)}{l.squad_numero ? ` · Squad ${l.squad_numero}` : ''}
                    </span>
                  </td>
                  {CAMPOS.map(([k, nome]) => (
                    <td key={k} className="px-1 py-2 text-center">
                      <input className={k === 'tempo' ? `${campo} w-20` : campo} inputMode="decimal" value={c[k] as string} disabled={c.dq}
                        aria-label={`${nome} de ${l.atirador}`} onChange={(e) => set(k, e.target.value)} />
                    </td>
                  ))}
                  <td className="px-1 py-2 text-center">
                    <input type="checkbox" className="w-5 h-5" checked={c.dq} aria-label={`DQ de ${l.atirador}`} onChange={(e) => set('dq', e.target.checked)} />
                  </td>
                  <td className={`${td} text-right whitespace-nowrap`}>
                    {l.resultado ? (l.resultado.dq ? <span className="text-red-400 font-semibold">DQ</span> : <>{l.resultado.pontos} pts<span className="block text-xs text-compumaq-textoSuave">HF {n4(l.resultado.hit_factor)}</span></>) : '—'}
                  </td>
                  <td className={`${td} whitespace-nowrap`}>
                    <button className="px-3 py-2 bg-compumaq-dourado text-compumaq-texto text-sm rounded-lg disabled:opacity-50" disabled={salvar.isPending}
                      onClick={() => salvar.mutate({ l, c })} aria-label={`Salvar ${l.atirador}`}>Salvar</button>
                    {l.resultado && (
                      <button className="ml-2 text-xs text-red-500" onClick={() => window.confirm(`Excluir o resultado de ${l.atirador} nesta pista?`) && apagar.mutate(l)}>Excluir</button>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        {linhas.length === 0 && <p className="text-center py-8 text-compumaq-textoSuave">Nenhum inscrito neste filtro.</p>}
      </div>
      <p className="text-xs text-compumaq-textoSuave">
        Pontos pelo Power Factor do atirador (Minor A5 C3 D1, Major A5 C4 D2); Miss, No-shoot e Procedimental −10 cada; a pista não fica negativa.
        HF = pontos ÷ tempo. DQ = desclassificado da prova.
      </p>
    </div>
  )
}
